import type { OnboardingAnswers, SaveDraftPayload } from "@shared/types";

const LOCAL_DRAFT_SCHEMA_VERSION = 1;
const STORAGE_PREFIX = "hvac-launch:onboarding-draft";

export interface LocalDraftRecord extends SaveDraftPayload {
  schemaVersion: number;
  baseRevision: number;
  mutationId: string;
  parentMutationId?: string;
  updatedAt: string;
  pending: boolean;
  conflicted?: boolean;
}

interface ServerDraft {
  answers: OnboardingAnswers;
  currentStep: string;
  revision: number;
  updatedAt?: string;
  lastMutationId?: string;
}

export function createLocalDraftRecord(input: {
  answers: OnboardingAnswers;
  currentStep: string;
  baseRevision: number;
  updatedAt?: string;
  mutationId?: string;
  parentMutationId?: string;
}): LocalDraftRecord {
  return {
    schemaVersion: LOCAL_DRAFT_SCHEMA_VERSION,
    answers: input.answers,
    currentStep: input.currentStep,
    baseRevision: input.baseRevision,
    mutationId: input.mutationId ?? crypto.randomUUID(),
    parentMutationId: input.parentMutationId,
    updatedAt: input.updatedAt ?? new Date().toISOString(),
    pending: true,
  };
}

export function chooseResumableDraft(input: {
  server: ServerDraft;
  local: LocalDraftRecord | null;
}): ServerDraft & {
  source: "local" | "server";
  preservedLocalDraft?: LocalDraftRecord;
} {
  const { server, local } = input;
  if (!local) return { ...server, source: "server" };
  if (server.answers.submitted) {
    return { ...server, source: "server", preservedLocalDraft: local };
  }
  const isSafeDescendant =
    server.revision > local.baseRevision &&
    server.lastMutationId === local.parentMutationId;
  if (local.conflicted && !isSafeDescendant) {
    return { ...server, source: "server", preservedLocalDraft: local };
  }
  if (!local.pending && !isSafeDescendant) {
    return { ...server, source: "server" };
  }
  if (server.revision > local.baseRevision) {
    if (server.lastMutationId === local.mutationId) {
      return { ...server, source: "server" };
    }
    if (server.lastMutationId !== local.parentMutationId) {
      return { ...server, source: "server", preservedLocalDraft: local };
    }
  }

  return {
    answers: local.answers,
    currentStep: local.currentStep,
    revision: server.revision,
    updatedAt: local.updatedAt,
    source: "local",
  };
}

export function localDraftStorageKey(inviteToken: string): string {
  let hash = 2166136261;
  for (let index = 0; index < inviteToken.length; index += 1) {
    hash ^= inviteToken.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${STORAGE_PREFIX}:${(hash >>> 0).toString(36)}`;
}

export function readLocalDraft(
  storage: Pick<Storage, "getItem">,
  key: string,
): LocalDraftRecord | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<LocalDraftRecord>;
    if (
      value.schemaVersion !== LOCAL_DRAFT_SCHEMA_VERSION ||
      !value.answers ||
      typeof value.currentStep !== "string" ||
      typeof value.baseRevision !== "number" ||
      typeof value.mutationId !== "string" ||
      typeof value.updatedAt !== "string" ||
      typeof value.pending !== "boolean"
    ) {
      return null;
    }
    return value as LocalDraftRecord;
  } catch {
    return null;
  }
}

export function writeLocalDraft(
  storage: Pick<Storage, "setItem">,
  key: string,
  record: LocalDraftRecord,
): void {
  try {
    storage.setItem(key, JSON.stringify(record));
  } catch {
    // Server autosave remains available when browser storage is unavailable.
  }
}

export function markLocalDraftSynced(
  storage: Pick<Storage, "setItem">,
  key: string,
  record: LocalDraftRecord,
  revision: number,
  updatedAt: string,
): LocalDraftRecord {
  const synced = {
    ...record,
    baseRevision: revision,
    updatedAt,
    pending: false,
  };
  writeLocalDraft(storage, key, synced);
  return synced;
}

export interface DraftSaveRequest extends SaveDraftPayload {
  expectedRevision: number;
  mutationId: string;
}

export interface DraftSaveResponse {
  revision: number;
  updatedAt: string;
}

export class DraftSyncCoordinator {
  private current: LocalDraftRecord | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private inFlightMutationId: string | null = null;
  private confirmedMutationId: string | undefined;
  private disposed = false;
  private readonly storage: Pick<Storage, "getItem" | "setItem">;
  private readonly storageKey: string;
  private readonly transport: (
    request: DraftSaveRequest,
    options: { keepalive: boolean },
  ) => Promise<DraftSaveResponse>;
  private readonly retryDelayMs: number;
  private readonly debounceDelayMs: number;
  private readonly onStateChange?: (
    state: "saving" | "saved" | "error" | "conflict",
  ) => void;

  revision: number;

  constructor(input: {
    initialRevision: number;
    initialMutationId?: string;
    storage: Pick<Storage, "getItem" | "setItem">;
    storageKey: string;
    transport: (
      request: DraftSaveRequest,
      options: { keepalive: boolean },
    ) => Promise<DraftSaveResponse>;
    retryDelayMs?: number;
    debounceDelayMs?: number;
    onStateChange?: (
      state: "saving" | "saved" | "error" | "conflict",
    ) => void;
  }) {
    this.revision = input.initialRevision;
    this.confirmedMutationId = input.initialMutationId;
    this.storage = input.storage;
    this.storageKey = input.storageKey;
    this.transport = input.transport;
    this.retryDelayMs = input.retryDelayMs ?? 2_000;
    this.debounceDelayMs = input.debounceDelayMs ?? 650;
    this.onStateChange = input.onStateChange;
  }

  stage(payload: SaveDraftPayload): LocalDraftRecord {
    const record = createLocalDraftRecord({
      ...payload,
      baseRevision: this.revision,
      parentMutationId:
        this.inFlightMutationId ?? this.confirmedMutationId,
    });
    this.current = record;
    writeLocalDraft(this.storage, this.storageKey, record);
    this.onStateChange?.("saving");
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.flush();
    }, this.debounceDelayMs);
    return record;
  }

  async flush(keepalive = false): Promise<void> {
    if (this.disposed || !this.current?.pending) return;
    if (this.inFlight) return await this.inFlight;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    const record = {
      ...this.current,
      baseRevision: this.revision,
    };
    this.current = record;
    writeLocalDraft(this.storage, this.storageKey, record);
    this.onStateChange?.("saving");

    const request = {
      answers: record.answers,
      currentStep: record.currentStep,
      expectedRevision: this.revision,
      mutationId: record.mutationId,
    };
    this.inFlightMutationId = record.mutationId;
    this.inFlight = this.transport(request, { keepalive })
      .then((result) => {
        this.revision = result.revision;
        this.confirmedMutationId = record.mutationId;
        if (this.current?.mutationId === record.mutationId) {
          this.current = markLocalDraftSynced(
            this.storage,
            this.storageKey,
            record,
            result.revision,
            result.updatedAt,
          );
          this.onStateChange?.("saved");
        } else {
          this.current = this.current
            ? { ...this.current, baseRevision: result.revision }
            : null;
          if (this.current) {
            writeLocalDraft(this.storage, this.storageKey, this.current);
          }
          setTimeout(() => void this.flush(), 0);
        }
      })
      .catch((error: unknown) => {
        const isConflict =
          error instanceof Error && error.name === "DraftSaveConflictError";
        if (isConflict && this.current) {
          this.current = { ...this.current, pending: false, conflicted: true };
          writeLocalDraft(this.storage, this.storageKey, this.current);
        }
        this.onStateChange?.(isConflict ? "conflict" : "error");
        if (!this.disposed && !isConflict) {
          this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            void this.flush();
          }, this.retryDelayMs);
        }
      })
      .finally(() => {
        this.inFlight = null;
        if (this.inFlightMutationId === record.mutationId) {
          this.inFlightMutationId = null;
        }
      });
    await this.inFlight;
  }

  acknowledgeCompletion(
    payload: SaveDraftPayload,
    result: DraftSaveResponse,
  ): void {
    this.revision = result.revision;
    const record = createLocalDraftRecord({
      ...payload,
      baseRevision: result.revision,
      updatedAt: result.updatedAt,
    });
    this.confirmedMutationId = record.mutationId;
    this.current = markLocalDraftSynced(
      this.storage,
      this.storageKey,
      record,
      result.revision,
      result.updatedAt,
    );
    this.onStateChange?.("saved");
  }

  dispose(): void {
    this.disposed = true;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }
}
